'use client'

import { useCallback, useState } from 'react'

import { Dialog } from '@graftcode/design-system'

import { CreateProjectForm } from '@common/modules/projects/forms'
import { useWorkspacesStore } from '@common/modules/workspaces'

import { ProjectCreatedSuccess } from '../ProjectCreatedSuccess'

import type { Props } from './CreateProjectDialog.types'

export function CreateProjectDialog({ open, onOpenChange, onAfterClose }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const currentWorkspace = useWorkspacesStore((s) => s.currentWorkspace)

  const [showSuccess, setShowSuccess] = useState(false)

  const handleSuccess = useCallback(
    (isFirstProject: boolean) => {
      if (isFirstProject) {
        setShowSuccess(true)
      } else {
        onOpenChange(false)
        onAfterClose?.()
      }
    },
    [onOpenChange, onAfterClose]
  )

  const handleCancel = useCallback(() => {
    onOpenChange(false)
    onAfterClose?.()
  }, [onOpenChange, onAfterClose])

  const handleContinue = useCallback(() => {
    setShowSuccess(false)
    onOpenChange(false)
    onAfterClose?.()
  }, [onOpenChange, onAfterClose])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isSubmitting) return
      onOpenChange(nextOpen)
      if (!nextOpen) {
        onAfterClose?.()
      }
    },
    [onOpenChange, isSubmitting, onAfterClose]
  )

  return (
    <Dialog
      noPadding
      open={open}
      ariaLabelledBy="project-dialog-title"
      ariaDescribedBy="project-dialog-description"
      showDismiss={!showSuccess}
      onOpenChange={showSuccess ? () => {} : handleOpenChange}
    >
      {showSuccess ? (
        <ProjectCreatedSuccess workspaceName={currentWorkspace?.name || ''} onContinue={handleContinue} />
      ) : (
        <CreateProjectForm onSuccess={handleSuccess} onCancel={handleCancel} onSubmittingChange={setIsSubmitting} />
      )}
    </Dialog>
  )
}
